const SDK_POLL_INTERVAL_MS = 100;
const SDK_TIMEOUT_MS = 15000;

let sdkLoadPromise: Promise<void> | null = null;

export class WafCaptchaRequiredError extends Error {
  constructor() {
    super("AWS WAF CAPTCHA challenge required");
    this.name = "WafCaptchaRequiredError";
  }
}

function sanitizeEnvValue(value: string): string {
  let sanitized = value.trim();
  if (
    (sanitized.startsWith("'") && sanitized.endsWith("'")) ||
    (sanitized.startsWith('"') && sanitized.endsWith('"'))
  ) {
    sanitized = sanitized.slice(1, -1).trim();
  }
  return sanitized;
}

function getJsApiUrl(): string {
  return sanitizeEnvValue(
    process.env.REACT_APP_AWS_WAF_JSAPI_URL ??
      process.env.REACT_APP_AWS_WAF_INTEGRATION_URL ??
      "",
  );
}

function getCaptchaApiKey(): string {
  return sanitizeEnvValue(process.env.REACT_APP_AWS_WAF_CAPTCHA_API_KEY || "");
}

function validateCaptchaApiKey(apiKey: string): void {
  if (!apiKey) {
    throw new Error("REACT_APP_AWS_WAF_CAPTCHA_API_KEY is not configured");
  }

  if (/\s/.test(apiKey)) {
    throw new Error(
      "AWS WAF CAPTCHA API key appears corrupted (contains whitespace). Re-copy the full key from the AWS WAF console CAPTCHA integration page.",
    );
  }
}

function formatWafCaptchaError(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (message.includes("api key") && message.includes("invalid")) {
      const host = window.location.hostname;
      return (
        `AWS WAF rejected the CAPTCHA API key for "${host}". ` +
        "Generate a new key in AWS WAF → Application integration → CAPTCHA integration (us-east-1) " +
        `and include "${host}" in the authorized domains. Ensure the key matches the same integration as REACT_APP_AWS_WAF_JSAPI_URL.`
      );
    }
    return error.message;
  }

  return "Unable to complete the security challenge. Please try again.";
}

export function isAwsWafConfigured(): boolean {
  return Boolean(getJsApiUrl() && getCaptchaApiKey());
}

export function loadAwsWafSdk(): Promise<void> {
  if (window.AwsWafIntegration) {
    return Promise.resolve();
  }

  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  const jsApiUrl = getJsApiUrl();
  if (!jsApiUrl) {
    return Promise.reject(
      new Error("REACT_APP_AWS_WAF_JSAPI_URL is not configured"),
    );
  }

  sdkLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[data-aws-waf-sdk="true"]`,
    );
    if (existingScript) {
      waitForAwsWafSdk().then(resolve).catch(reject);
      return;
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = jsApiUrl;
    script.defer = true;
    script.dataset.awsWafSdk = "true";
    script.onload = () => {
      waitForAwsWafSdk().then(resolve).catch(reject);
    };
    script.onerror = () => {
      sdkLoadPromise = null;
      reject(new Error("Failed to load AWS WAF JavaScript SDK"));
    };
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
}

export async function waitForAwsWafSdk(): Promise<void> {
  const start = Date.now();

  while (!window.AwsWafIntegration) {
    if (Date.now() - start > SDK_TIMEOUT_MS) {
      throw new Error("Timed out waiting for AWS WAF JavaScript SDK");
    }
    await new Promise((resolve) => setTimeout(resolve, SDK_POLL_INTERVAL_MS));
  }
}

export async function ensureWafToken(): Promise<void> {
  await waitForAwsWafSdk();
  await window.AwsWafIntegration!.getToken();
}

export async function wafFetch(
  url: string,
  init?: RequestInit,
): Promise<Response> {
  await ensureWafToken();
  const headers = {
    ...(init?.headers || {}),
  };

  // Prefer credentials so Set-Cookie (verification-token) is stored on success.
  // WAF challenge responses often use Access-Control-Allow-Origin: *, which
  // blocks credentialed reads — fall back to omit so HTTP 405 is visible and
  // the CAPTCHA modal can open. After the puzzle, the retry hits the API with
  // include again and the cookie is set.
  try {
    return await window.AwsWafIntegration!.fetch(url, {
      ...init,
      credentials: "include",
      headers,
    });
  } catch {
    return window.AwsWafIntegration!.fetch(url, {
      ...init,
      credentials: "omit",
      headers,
    });
  }
}

export async function waitForAwsWafCaptcha(): Promise<void> {
  await waitForAwsWafSdk();

  const start = Date.now();
  while (!window.AwsWafCaptcha) {
    if (Date.now() - start > SDK_TIMEOUT_MS) {
      throw new Error("Timed out waiting for AWS WAF CAPTCHA API");
    }
    await new Promise((resolve) => setTimeout(resolve, SDK_POLL_INTERVAL_MS));
  }
}

export function isWafCaptchaChallenge(response: Response): boolean {
  return response.status === 405;
}

export async function renderWafCaptcha(container: Element): Promise<string> {
  const apiKey = getCaptchaApiKey();
  validateCaptchaApiKey(apiKey);

  await loadAwsWafSdk();
  await waitForAwsWafCaptcha();

  return new Promise((resolve, reject) => {
    window.AwsWafCaptcha!.renderCaptcha(container, {
      apiKey,
      dynamicWidth: true,
      skipTitle: true,
      onSuccess: (wafToken: string) => resolve(wafToken),
      onError: (error: Error) => reject(error),
    });
  });
}

export { formatWafCaptchaError, getCaptchaApiKey, validateCaptchaApiKey };
